import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Anchor, Sparkles, ArrowRight, Rocket, CheckCircle2, LogIn } from 'lucide-react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { auth, db, doc, getDoc, setDoc, serverTimestamp, onAuthStateChanged, signInWithPopup, googleProvider } from '../firebase';
import { toast } from 'sonner';

export default function Success() {
  const [searchParams] = useSearchParams();
  const [founderNumber, setFounderNumber] = useState<number | null>(null);
  const [isUpdating, setIsUpdating] = useState(true);
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();

  const plan = searchParams.get('plan') || 'pro';

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (u) {
        handleUpgrade(u.uid, u.email);
      } else {
        setIsUpdating(false);
      }
    });
    return unsubscribe;
  }, []);

  const handleUpgrade = async (uid: string, email: string | null) => {
    try {
      await setDoc(doc(db, 'users', uid), {
        plan: plan,
        isPro: plan === 'pro',
        updatedAt: serverTimestamp(),
        email: email
      }, { merge: true });
      
      toast.success(`Account upgraded to ${plan.toUpperCase()}!`);
      setIsUpdating(false);
    } catch (error) {
      console.error('Failed to upgrade account:', error);
      toast.error('Failed to update account status. Please contact support.');
      setIsUpdating(false);
    }
  };

  const handleLogin = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Login failed:', error);
      toast.error('Login failed. Please try again.');
    }
  };

  useEffect(() => {
    setFounderNumber(Math.floor(Math.random() * 15) + 35);
  }, []);

  if (isUpdating) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-8">
        <motion.div 
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-zinc-800 border-t-blue-500 rounded-full mb-8"
        />
        <p className="font-mono text-sm uppercase tracking-widest text-zinc-500 animate-pulse">Synchronizing Lab Credentials...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-8 text-center">
        <div className="w-20 h-20 bg-blue-600 rounded-3xl flex items-center justify-center mb-8 shadow-[0_0_50px_rgba(37,99,235,0.2)]">
          <CheckCircle2 size={40} className="text-white" />
        </div>
        <h1 className="text-4xl font-mono uppercase tracking-tighter mb-4">Payment Successful</h1>
        <p className="text-zinc-400 max-w-md mb-12 font-light leading-relaxed">
          Your payment was processed successfully. Please log in with your Google account to link your {plan.toUpperCase()} plan to your laboratory profile.
        </p>
        <button
          onClick={handleLogin}
          className="flex items-center gap-3 px-8 py-4 bg-white text-black rounded-xl font-mono uppercase text-sm font-bold tracking-widest hover:scale-[1.02] transition-all"
        >
          <LogIn size={18} />
          Link My Account
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-zinc-100 font-sans flex items-center justify-center p-4 md:p-8">
      <div className="max-w-2xl w-full text-center space-y-8">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", damping: 12 }}
          className="relative bg-zinc-900 border-4 border-zinc-800 p-8 md:p-12 rounded-[48px] overflow-hidden shadow-2xl"
        >
          {/* Certificate Background Elements */}
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600" />
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-purple-600/10 rounded-full blur-3xl" />
          
          <div className="relative z-10 space-y-6">
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-900/20">
                <Anchor size={32} className="text-white" />
              </div>
            </div>
            
            <div className="space-y-2">
              <h2 className="text-[10px] font-mono uppercase tracking-[0.4em] text-blue-500 font-bold">Official Credentials</h2>
              <h1 className="text-3xl md:text-4xl font-mono uppercase tracking-tighter text-zinc-100">Plank Owner</h1>
            </div>

            <div className="py-6 border-y border-zinc-800/50 space-y-4">
              <p className="text-xs text-zinc-400 font-light leading-relaxed max-w-sm mx-auto">
                This document certifies that <span className="text-zinc-100 font-medium">{user.email}</span> has secured a permanent berth in the Yacht Labs ecosystem.
              </p>
              <div className="flex items-center justify-center gap-8">
                <div>
                  <p className="text-[8px] font-mono uppercase text-zinc-600 mb-1">Founder No.</p>
                  <p className="text-xl font-mono text-zinc-100">#{founderNumber?.toString().padStart(4, '0')}</p>
                </div>
                <div className="w-px h-8 bg-zinc-800" />
                <div>
                  <p className="text-[8px] font-mono uppercase text-zinc-600 mb-1">Status</p>
                  <p className="text-xl font-mono text-green-500">ACTIVE</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-zinc-950 border border-zinc-800 rounded-full">
                <Sparkles size={12} className="text-blue-500" />
                <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Pro Access Activated</span>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="flex flex-col md:flex-row items-center justify-center gap-4">
          <Link 
            to="/studio"
            className="w-full md:w-auto flex items-center justify-center gap-3 px-8 py-4 bg-zinc-100 text-zinc-950 rounded-2xl font-mono uppercase text-xs font-bold tracking-widest hover:bg-white transition-all shadow-xl"
          >
            Enter the Studio
            <ArrowRight size={16} />
          </Link>
          <button 
            onClick={() => window.print()}
            className="w-full md:w-auto px-8 py-4 border border-zinc-800 text-zinc-500 rounded-2xl font-mono uppercase text-xs tracking-widest hover:text-zinc-100 hover:bg-zinc-900 transition-all"
          >
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
}
